'use client';
import type { ComponentPropsWithoutRef } from 'react';
import { Controller, type FieldPath, type FieldValues, useFormContext } from 'react-hook-form';

import { Label } from '@/slw/shared/ui/label';
import { RadioGroup as BaseRadioGroup, RadioGroupItem } from '@/slw/shared/ui/radio-group';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/slw/shared/ui/base-form';
import { cn } from '@/slw/shared/lib/utils';
import { SelectOption } from '@/slw/shared/types/common.type';

// ----------------------------------------------------------------------

interface RHFRadioGroupProps<TFieldValues extends FieldValues>
  extends Omit<ComponentPropsWithoutRef<typeof Controller>, 'name' | 'control' | 'render'>,
    Omit<ComponentPropsWithoutRef<typeof BaseRadioGroup>, 'name' | 'defaultValue' | 'onChange'> {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: string;
  label?: string;
  withError?: boolean;
  onChange?: (value: string | boolean) => void;
  radioGroupItemClass?: string;
  options: SelectOption[];
  showMessError?: boolean;
  direction?: 'row' | 'column';
  labelClass?: string;
}

export function RadioGroup<TFieldValues extends FieldValues>({
  name,
  helperText,
  showMessError,
  label,
  className,
  containerClass,
  radioGroupItemClass,
  withError = true,
  defaultValue,
  labelClass,
  options,
  onChange,
  direction = 'row',
  ...other
}: RHFRadioGroupProps<TFieldValues>) {
  const { control } = useFormContext();
  const directionClass =
    direction === 'row' ? 'gap-2 items-center flex space-y-0' : 'gap-1 space-y-1 flex flex-col items-start';

  return (
    <FormField
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <FormItem className={cn(directionClass, containerClass)}>
          {!!label && (
            <FormLabel className={cn('whitespace-nowrap', 'h-4', labelClass)} htmlFor={other.id ?? name}>
              {label}
            </FormLabel>
          )}
          <BaseRadioGroup
            {...field}
            onValueChange={(e) => {
              field.onChange(e);
              onChange && onChange(e);
            }}
            value={field.value}
            className={cn(
              'group/radio peer',
              className,
              error && withError && 'border-destructive focus-visible:ring-destructive',
            )}
            data-state-error={!!error}
            aria-invalid={!!error}
          >
            {options.map((option) => {
              return (
                <div
                  key={option.value}
                  className={cn('flex items-center space-x-1 lg:space-x-2', radioGroupItemClass)}
                >
                  <RadioGroupItem
                    checked={option.value === field.value}
                    value={option.value ?? ''}
                    id={option.value ?? ''}
                    className={cn(
                      'group-data-[state-error=true]/radio:border-destructive',
                      'group-data-[state-error=true]/radio:text-destructive',
                    )}
                  />
                  <Label
                    className='capitalize group-data-[state-error=true]/radio:text-destructive'
                    htmlFor={option.value ?? ''}
                  >
                    {option.label}
                  </Label>
                </div>
              );
            })}
          </BaseRadioGroup>
          {helperText && withError && showMessError && (
            <p className='text-sm text-muted-foreground ml-1.5'>{helperText}</p>
          )}
          {error && withError && showMessError && <FormMessage>{error.message}</FormMessage>}
        </FormItem>
      )}
    />
  );
}
